<template>
  <div class="goal-analysis-box-container">
    <div class="goal-analysis-header">
      <h4>{{ cmsAnalysisType }}</h4>
      <button
        v-if="shouldDisplayAnalysis"
        class="btn btn-primary-minus btn-xs"
        type="button"
        @click="hideAnalysis"
        data-test="hide-analysis-button"
      >
        Hide
      </button>
      <button
        v-else
        class="btn btn-primary-plus btn-xs"
        type="button"
        @click="showAnalysis = true"
        data-test="show-analysis-button"
      >
        Show
      </button>
    </div>
    <div class="goal-analysis-container" :class="{ 'center-flex': !shouldDisplayAnalysis }">
      <div v-if="shouldDisplayAnalysis" class="goal-analysis-text-box">
        <div v-if="!isLoading" v-markdown="text" data-test="goal-analysis-text"></div>
        <LoadingSpinner v-else :type="'Dots'" size="100px" data-test="goal-suggestion-loading" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, Ref, ref } from "vue";
import axios, { AxiosError } from "axios";
import LoadingSpinner from "../loading-spinner.vue";
import processPromptVars, { getDataContext } from "../smart-scratchpad/process-prompt-vars";
import { renderErrorMessageToClient } from "../smart-scratchpad/variable-error-helpers";
import chat from "../smart-scratchpad/chat";
import { TeamGoalAnalysisApiModel } from "../../api-models";
import { Analytics } from "../../services/analytics/analytics";
import { csContext } from "../../services/cs-context";

const isTeamLead = inject("isTeamAdmin") as Ref<boolean>;

const props = defineProps<{
  goalId: string;
  goalInput: string;
  prompt: string;
  analysis?: TeamGoalAnalysisApiModel;
  cmsAnalysisType: string;
}>();

const emit = defineEmits(["update:analysis"]);

const text = ref<string>("");
const promptInProgress = ref(false);
const showAnalysis = ref(false);

const isLoading = computed(() => promptInProgress.value);
const goalInput = computed(() => props.goalInput);
const shouldDisplayAnalysis = computed(
  () => showAnalysis.value && (text.value.length > 0 || promptInProgress.value),
);

let dataContext: any = null;

const promptWithGoalInput = computed(() => {
  return `@gpt-4\n\nPlease format your response including string formatting, new lines and bullet points where appropriate. This is the readers written team goal: ${goalInput.value}\n\n${props.prompt}`;
});

async function saveAnalysis() {
  if (!isTeamLead.value) return;
  await axios.post("/api/teamgoalanalysis", {
    goalId: props.goalId,
    goalAnalysis: {
      analysis: text.value,
      analysisType: props.cmsAnalysisType,
    },
  });
}

async function askPrompt(): Promise<void> {
  // const activeVersion = version;
  promptInProgress.value = true;

  if (promptWithGoalInput.value?.trim().length > 0) {
    const { processed, errors } = await processPromptVars(promptWithGoalInput.value, dataContext);

    if (errors.length > 0) {
      const errorMessage = renderErrorMessageToClient(errors);
      text.value = errorMessage.includes("Sorry, I couldn't find any data on")
        ? "This will be available once you have at least two members in your team."
        : errorMessage;

      promptInProgress.value = false;
      return;
    }

    Analytics.action("Ran Goal Analysis", "GoalAnalysisActions", {
      teamLeadId: csContext.user.id,
      teamId: csContext.currentTeam?.id,
      goalId: props.goalId,
    });

    chat(processed, (content) => {
      // if (activeVersion === version) {
      promptInProgress.value = false;
      text.value = content.trim();
      // TODO: ensure that this component gets the analysis type even when an analysis doesn't exist
      emit("update:analysis", text.value, props.cmsAnalysisType);
      // }
    })
      .then((message) => {
        Analytics.action("Completed Goal Analysis", "GoalAnalysisActions", {
          teamLeadId: csContext.user.id,
          teamId: csContext.currentTeam?.id,
          goalId: props.goalId,
        });

        saveAnalysis();
      })
      .catch((e) => {
        text.value = "Sorry, there was an error. Please refresh the page.";
        const error = e as AxiosError;
        const errorMessage = `${error.response?.status}: ${error.response?.statusText}`;

        Analytics.action("Failed Goal Analysis", "GoalAnalysisActions", {
          teamLeadId: csContext.user.id,
          teamId: csContext.currentTeam?.id,
          goalId: props.goalId,
          error: errorMessage,
        });
      });
  }
}

async function runAnalysis() {
  if (promptInProgress.value) return;
  if (!dataContext) dataContext = getDataContext();
  await askPrompt();
}

function hideAnalysis() {
  showAnalysis.value = false;
}

onMounted(async () => {
  if (props.analysis?.analysis) {
    text.value = props.analysis.analysis;
  } else if ((text.value === "" || text.value.length < 200) && isTeamLead.value) {
    await runAnalysis();
  }
});
</script>
<style scoped lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.goal-analysis-container {
  display: flex;
  align-items: center;

  img {
    margin-right: 10px;
    width: 30px;
  }
}

.goal-analysis-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
}

.goal-analysis-box-container {
  background-color: @off-white;
  border-radius: 5px;
  margin-bottom: 10px;
}

.goal-analysis-text-box {
  padding: 6px 24px 20px;
  resize: none;
  font-size: 16px;
  position: relative;
  width: 100%;
  //min-height: 100px;

  p {
    margin: 0;
  }
}
</style>
<style lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.goal-analysis-text-box {
  strong {
    color: #333333;
  }
}
</style>
